/**
 * Pure functions and constants for org/team path handling.
 * Safe to import from middleware (Edge Runtime) — no React hooks.
 */

/**
 * Build an authenticated path with org/team route segments.
 *
 * @param path  - The logical route, e.g. "/files" or "/settings/general"
 * @param ctx   - { orgId, teamId } from provider context or route params
 */
export function buildOrgTeamPath(path: string, ctx: { orgId: string; teamId: string }): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `/${ctx.orgId}/${ctx.teamId}${normalizedPath}`;
}

/**
 * Strip the orgId/teamId prefix from a pathname so it can be compared
 * against logical routes like "/files" or "/settings/general".
 *
 * Handles both localized (/en/orgId/teamId/files) and non-localized (/orgId/teamId/files) paths.
 * Also handles pathnames that are already stripped (no prefix to remove).
 */
export function stripOrgTeamPrefix(pathname: string): string {
  // Remove optional locale prefix (/en, /es)
  const withoutLocale = pathname.replace(/^\/[a-z]{2}(?=\/)/, "");

  // Remove the two leading segments (orgId/teamId)
  const stripped = withoutLocale.replace(/^\/[^/]+\/[^/]+/, "");

  return stripped || "/";
}

/**
 * Known bare routes that belong under the [orgId]/[teamId] segment.
 * Used by middleware to detect bare URLs that need a redirect.
 */
export const AUTHENTICATED_ROUTE_PREFIXES = [
  "files",
  "chat",
  "dashboard",
  "usage-dashboard",
  "assigned-to-me",
  "shared-with-me",
  "archive",
  "profile",
  "settings",
  "crunches",
  "checklists",
  "grouped-projects",
  "add-file",
  "invite",
  "move",
  "create-team",
  "trials",
  "tasks",
] as const;
